export type QueryParams = URLSearchParams | Record<string, any>

export type WebEvent = {
    name: string
    date: Date
}

export type SyncReportEvent = WebEvent & {
    countId: number
    iterationId: number
    locationId: string
}

export type DeserializeItemEvent = WebEvent & {
    sku: string
}

export type UpdateVersionEvent = WebEvent & {
    version: string
}

export type Options = {
    onOpen?: (event: Event) => void
    onError?: (err: Event) => void
    onClose?: (event: CloseEvent) => void
    onSyncReport?: (event: SyncReportEvent) => void
    onDeserializeItem?: (event: DeserializeItemEvent) => void
    onUpdateVersion?: (event: UpdateVersionEvent) => void
}

/* Most techgo messages will be less than a kilobyte. Allowing 10,000 in the queue would require around 10M
 * of memory. 10,000 queued messages can last for hours without a connection. This is plenty. The queue
 * is flushed whenever a connection is opened or when manually closed.
 */
const MAX_QUEUE_SIZE = 10000

/* Reconnecting uses a backoff strategy with jitters. There is no limit on attempts. We backoff up to the
 * max wait period between "reconnect" attempts.
 */
const RECONNECT_WAIT_PERIOD = 500
const MAX_RECONNECT_WAIT_PERIOD = 5000

/* Heartbeat is very similar to TCP Keep-Alive. The difference is that Keep-Alive is between one TCP
 * stack and another, which in most cases means between the client and a gateway or proxy. Heartbeat
 * is between the client and the final destination server, as it is the application layer. If we don't
 * receive a graceful TCP shutdown from the server, techgo could go awhile without knowing the connection
 * is severed. Heartbeat fixes this issue. It also keeps the connection active, avoiding APIGateway's
 * 10-minute idle timeout for websockets.
 */
const HEARTBEAT_MSG = 'Heartbeat'
const HEARTBEAT_ACK_MSG = 'Heartbeat ACK'
const HEARTBEAT_INTERVAL = 30000
const HEARTBEAT_TIMEOUT = 8000

/* H3 WebEvents microservice client, implemented using native WebSockets. Note that APIGateway is a pure
 * WebSocket server implementation: socket.io clients don't work with it.
 */
class WebEvents {
    private webSocket: WebSocket | null = null
    private messageQueue: string[] = []
    private options: Options = {}
    private heartbeatInterval: any = null
    private heartbeatAckTimer: any = null
    private reconnectTimer: any = null
    private reconnectAttempts = 0
    private webSocketUrl: string = ''

    public connect(url: string, queryParams?: QueryParams): WebEvents {
        if (this.webSocket !== null)
            return this

        clearTimeout(this.reconnectTimer)

        this.webSocketUrl = `${url}${this.toQueryString(queryParams)}`
        this.webSocket = new WebSocket(this.webSocketUrl)

        this.webSocket.onopen = (e: Event) => {
            console.debug('OPENED', new Date().toLocaleString())

            this.flush() // flush the queue
            this.reconnectAttempts = 0

            if (this.options.onOpen) this.options.onOpen(e)

            this.startHeartbeat()
        }

        this.webSocket.onmessage = (e: MessageEvent) => {
            console.debug('RECEIVED', e.data)

            if (e.data === HEARTBEAT_ACK_MSG)
                clearTimeout(this.heartbeatAckTimer)
            else this.processMessage(e.data)
        }

        this.webSocket.onerror = (e: Event) => {
            console.debug('ERROR', e)
            if (this.options.onError) this.options.onError(e)
        }

        this.webSocket.onclose = (e: CloseEvent) => {
            this.closeHandler(e)
        }

        return this
    }

    public info(message: string, context?: Record<string, any>): void {
        this.log('info', message, context)
        console.info(message, context ? JSON.stringify(context) : '')
    }

    public warning(message: string, context?: Record<string, any>): void {
        this.log('warning', message, context)
        console.warn(message, context ? JSON.stringify(context) : '')
    }

    public error(
        message: string,
        context?: Record<string, any>,
        error?: any
    ): void {
        this.log('error', message, context, error)
        console.error(
            message,
            context ? JSON.stringify(context) : '',
            error ? JSON.stringify(this.parseErrorStack(error)) : ''
        )
    }

    public alert(
        message: string,
        context?: Record<string, any>,
        error?: any
    ): void {
        this.log('alert', message, context, error)
        console.error(
            message,
            context ? JSON.stringify(context) : '',
            error ? JSON.stringify(this.parseErrorStack(error)) : ''
        )
    }

    public debug(
        message: string,
        context?: Record<string, any>,
        error?: any
    ): void {
        this.log('debug', message, context, error)
        console.debug(
            message,
            context ? JSON.stringify(context) : '',
            error ? JSON.stringify(this.parseErrorStack(error)) : ''
        )
    }

    public log(
        level: string,
        message: string,
        context?: Record<string, any>,
        error?: any
    ): void {
        let payload = {
            action: 'log',
            logged_at: new Date().toISOString(),
            level,
            message,
            stack: this.parseErrorStack(error),
            context
        }

        console.log(payload)
        this.send(JSON.stringify(payload))
    }

    private send(message: string): void {
        if (this.isConnected()) {
            console.debug('SEND', message)
            this.webSocket?.send(message)
        } else {
            this.queueMessage(message)
        }
    }

    public setOptions(options?: Options): WebEvents {
        this.options = options ?? {}
        return this
    }

    public mergeOptions(options: Options): WebEvents {
        this.options = {...this.options, ...options}
        return this
    }

    public isConnected(): boolean {
        return this.webSocket?.readyState === WebSocket.OPEN
    }

    public close(): void {
        if (this.webSocket === null) return

        /* try to flush the queue */
        this.flush()

        this.messageQueue = []
        this.webSocket.onclose = null
        this.webSocket.close() // can take up to 30 seconds due to WebSocket implementation
        this.closeHandler()
    }

    private parseErrorStack(error: any): string[] | undefined {
        try {
            return error?.stack
                ?.split('\n')
                ?.map((x: string) => x.trim())
                ?.filter((x: string) => x.startsWith('at '))
        } catch {
            return
        }
    }

    private processMessage(message: string) {
        let event: any

        try {
            event = JSON.parse(message)
        } catch (error: any) {
            this.error(`Failed to parse message from server: ${error}`, {message}, error)
        }

        switch (event.name ?? '') {
            case 'sync-report':
                if (this.options.onSyncReport)
                    this.options.onSyncReport(event)
                break

            case 'deserialize-item':
                if (this.options.onDeserializeItem)
                    this.options.onDeserializeItem(event)
                break

            case 'update-version':
                if (this.options.onUpdateVersion)
                    this.options.onUpdateVersion(event)
                break

            case 'are-you-there':
                break // ignore

            default:
                this.warning(`Unknown message from server`, {
                    raw_message: message
                })
                break
        }
    }

    private closeHandler(event: CloseEvent | null = null): void {
        if (this.webSocket === null) return

        console.debug('CLOSED', new Date().toLocaleString())

        let url = this.webSocket.url

        this.webSocket = null
        this.stopHeartbeat()
        clearTimeout(this.reconnectTimer)

        /* caller was onclose event handler */
        if (event) {
            if (this.options.onClose) this.options.onClose(event)
            this.reconnect()
        }
    }

    private reconnect(): void {
        /* Use a backoff strategy with jitter: start at ~RECONNECT_WAIT_PERIOD and increment by
         * (RECONNECT_WAIT_PERIOD / 2) until MAX_RECONNECT_WAIT_PERIOD is reached. Retries continue
         * until a connection is made or the socket is manually closed.
         */
        let jitter = Math.random() * 9
        let wait =
            this.reconnectAttempts * (RECONNECT_WAIT_PERIOD / 2) +
            RECONNECT_WAIT_PERIOD +
            jitter

        this.reconnectTimer = setTimeout(() => {
            if (this.webSocket === null) {
                this.reconnectAttempts++
                console.debug(
                    `RECONNECT ${this.reconnectAttempts}`,
                    new Date().toLocaleString()
                )
                this.connect(this.webSocketUrl)
            }
        }, Math.min(MAX_RECONNECT_WAIT_PERIOD, wait))
    }

    private queueMessage(message: string): void {
        /* never queue a heartbeat message */
        if (message === HEARTBEAT_MSG) return

        if (this.messageQueue.length > MAX_QUEUE_SIZE) {
            let m = this.messageQueue.shift()
            console.debug(
                'WARN',
                `message queue exceeded, removing oldest message: ${m}`
            )
        }

        console.debug('QUEUE', message)
        this.messageQueue.push(message)
    }

    private flush(): void {
        if (this.isConnected())
            while (this.messageQueue.length)
                this.webSocket?.send(this.messageQueue.shift() as string)
    }

    private startHeartbeat(): void {
        this.heartbeatInterval = setInterval(() => {
            this.send(HEARTBEAT_MSG)

            /* reconnect if there is no heartbeat ACK after HEARTBEAT_TIMEOUT */
            this.heartbeatAckTimer = setTimeout(() => {
                console.debug('ERROR', 'no heartbeat response, reconnecting')
                this.close()
                this.connect(this.webSocketUrl)
            }, HEARTBEAT_TIMEOUT)
        }, HEARTBEAT_INTERVAL)
    }

    private stopHeartbeat(): void {
        clearInterval(this.heartbeatInterval)
        clearTimeout(this.heartbeatAckTimer)
    }

    private toQueryString(queryParams?: QueryParams): string {
        if (!queryParams) return ''

        if (!(queryParams instanceof URLSearchParams))
            queryParams = new URLSearchParams(queryParams)

        let query = queryParams.toString()
        return query ? '?' + query : ''
    }
}

export const webEvents: WebEvents = new WebEvents()
